import React, {
  useEffect,
  useReducer,
  useState,
  useCallback,
  useRef,
} from "react";
import { useSelector } from "react-redux";

import { useNavigate } from "react-router-dom";
import { R_GAME_SCREEN } from "../constants/routes";
import moment from "moment";
import * as analytics from "../helpers/analyticsHelpers";
import useFetch from "../hooks/useFetch";
import {
  API_SEND_AUTOMATION,
  API_SHOP_PRODUCTS,
  API_Q_USERS,
} from "../constants/api";

const defaultContext = {};
const SystemContext = React.createContext(defaultContext);

const KEY_TOGGLE_LOGGED = "KEY_TOGGLE_LOGGED";
const KEY_SET_SCROLLABLE = "KEY_SET_SCROLLABLE";
const KEY_TOGGLE_ASIDE = "KEY_TOGGLE_ASIDE";
const KEY_SET_URL_GAME = "KEY_SET_URL_GAME";

const reducer = (state, action) => {
  switch (action.type) {
    case KEY_TOGGLE_LOGGED:
      return {
        ...state,
        logged: !state.logged,
      };
    case KEY_SET_SCROLLABLE:
      return {
        ...state,
        isScrollable: action.isScrollable,
      };
    case KEY_TOGGLE_ASIDE:
      return {
        ...state,
        isAsideOpen: action.updateAside,
        isScrollable: action.isScrollable,
      };
    case KEY_SET_URL_GAME:
      return {
        ...state,
        gameUrl: action.url,
      };
    default:
      return { ...state };
  }
};

export const SystemProvider = ({ children }) => {
  const { activeUser, session } = useSelector((state) => state.session);
  const userAvatar = activeUser ? activeUser.avatar : session?.my_user?.avatar;
  analytics.initSession(session, activeUser);
  const configs = useSelector((state) => state.configs);

  const [loginInfo, setLoginInfo] = useState(null);

  const [my_users, isLoadingUsers, , getUsers] = useFetch({
    url: API_Q_USERS,
    autoInit: false,
  });

  const [localUsers, setLocalUsers] = useState(null);

  const [activeUsersCount, setActiveUsersCount] = useState(0);

  useEffect(() => {
    if (session && !my_users) {
      getUsers({ url: API_Q_USERS });
    }
  }, [session]);

  useEffect(() => {
    if (my_users) {
      setLocalUsers(my_users);
      const actives = my_users.filter(
        (u) => u.parent_activity_status === "true",
      ).length;
      setActiveUsersCount(actives);
    }
  }, [my_users]);

  const [modalFreeLimit, setModalFreeLimit] = useState(null);

  const [preventFreeOpenGame, setPreventFreeOpenGame] = useState(false);
  const { activities } = useSelector((state) => state.activities);

  const [isRegister, setIsRegister] = useState(false);
  const [isPurchase, setIsPurchase] = useState(false);

  const [UserCards, setUserCards] = useState();
  const [PixResponse, setPixResponse] = useState();
  const [NeedUpdateSession, setNeedUpdateSession] = useState(false);

  const [PurchaseInfo, setPurchaseInfo] = useState({
    MethodChosen: null,
    SuccessState: "processing",
    customer_id: null,
    CreditCard: {
      type: "creditcard",
      number: "",
      cvvNumber: "",
      expMonth: "",
      expYear: "",
    },
    OldCards: {
      type: "creditcard",
      card_id: "",
    },
    DescPix: {
      type: "pix",
    },
  });
  const initialError = {
    invoiceData: false,
    invoiceShowError: false,
    creditCard: false,
    creditCardShowError: false,
    pix: false,
    pixShowError: false,
  };
  const [CheckError, setCheckError] = useState(initialError);
  const initialInfoUser = {
    isGold: null,
    isSilver: null,
    isBase: null,
    isTrial: null,
    isFree: null,
    usersTotal: 0,
    usersUsed: 0,
    usersAvailable: 0,
    dateExpiration: null,
    paymentMethod: null,
    hasLastPayment: null,
    lastPayment: null,
    remainingDays: 0,
  };
  const [infoUsers, setInfoUsers] = useState(null);
  const [actualPlan, setActualPlan] = useState();
  const [activePlans, setActivePlans] = useState(null);
  const [productBase, setProductBase] = useState(null);
  const OnboardingViewed = localStorage.getItem("onboarding");
  const [openModalStart, setOpenModalStart] = useState(false);
  const [openModalWaitPayment, setOpenModalWaitPayment] = useState(false);
  const prevSubRef = useRef(null);
  const initialState = {
    logged: false,
    isScrollable: true,
    isAsideOpen: false,
    gameUrl: false,
  };
  const initialErrorRule = {
    number: null,
    cvvNumber: null,
    expMonth: null,
    expYear: null,
    checkbox: false,
  };

  const [
    updateTag,
    onErrorUpdateTag,
    isprogressingUpdateTag,
    fetchUpdateTagUser,
  ] = useFetch({
    autoInit: false,
  });

  // Hook para buscar todos os produtos da loja (não inicia automaticamente)
  const [
    shopProducts, // Dados retornados da API (produtos da loja)
    onErrorShopProducts, // Flag/estado de erro
    isLoadingShopProducts, // Flag de loading
    fetchShopProducts, // Função para disparar a requisição
  ] = useFetch({
    autoInit: false,
  });

  const accountUser = session
    ? {
        ...(session.my_user ?? {}),
        sub: session.my_user?.sub ?? session.sub,
        given_name:
          session.my_user?.given_name ??
          session.given_name ??
          session.email ??
          "Meu perfil",
        email: session.my_user?.email ?? session.email,
        avatar: session.my_user?.avatar ?? session.avatar,
        parent_activity_status:
          session.my_user?.parent_activity_status ?? "true",
      }
    : null;

  // Define o usuário atual: prioriza activeUser, senão pega da sessão
  const currentUser = activeUser
    ? {
        ...activeUser,
        isMainProfile: false,
        profile: "patient",
        typePlan: accountUser.typePlan,
        typeProfile: "patient_" + accountUser.typePlan,
      }
    : { ...accountUser, isMainProfile: true };

  // Armazena os avatares por usuário (estrutura: [{ sub, avatar }])
  const [currentAvatar, setCurrentAvatar] = useState([]);

  // Função para atualizar os produtos gerais da loja
  const updateShopProducts = useCallback(() => {
    if (!isLoadingShopProducts && currentUser?.sub) {
      fetchShopProducts({
        url: API_SHOP_PRODUCTS(currentUser.sub),
        method: "GET",
      });
    }
  }, [currentUser, isLoadingShopProducts]);

  // Sempre que o usuário atual mudar:
  useEffect(() => {
    if (!currentUser) return; // Evita executar sem usuário definido
    const currentSub = currentUser.sub;
    const previousSub = prevSubRef.current;
    prevSubRef.current = currentSub;

    // Garante que cada usuário tenha seu avatar armazenado
    // Evita duplicar entrada se já existir no estado
    setCurrentAvatar((prev) => {
      const alreadyExists = prev.some((item) => item.sub === currentUser.sub);

      if (alreadyExists) {
        return prev; // Se já existe, mantém como está
      }

      // Se não existe, adiciona novo registro
      return [
        ...prev,
        {
          sub: currentSub,
          avatar: currentUser.avatar,
        },
      ];
    });
    if (!previousSub) return;

    // currentUser.sub é primitivo — o efeito só dispara quando o usuário realmente troca
  }, [currentUser?.sub]);

  const tagMapToSend = {
    thanks_page: "Obrigado",
  };

  const clearTagLocal = () => {
    const stateTagBuyString = localStorage.getItem("tag_manage");
    if (stateTagBuyString && stateTagBuyString.length > 0) {
      try {
        const stateTagBuy = JSON.parse(stateTagBuyString);
        const keyToDelete =
          session.sub + "_" + moment().format("YYYY-MM-DD HH");
        delete stateTagBuy[keyToDelete];
        localStorage.setItem("tag_manage", JSON.stringify({ ...stateTagBuy }));
      } catch (e) {
        console.log("Error on clearTagLocal:", e);
      }
    }
  };
  const tagRemaingToSend = [];

  useEffect(() => {
    if (
      session &&
      session.sub &&
      tagRemaingToSend.length > 0 &&
      updateTagUser
    ) {
      console.log("User not logged in");
      updateTagUser(tagRemaingToSend);
    }
  }, [session]);

  const updateTagUser = (tags_names = [], fields_names = {}) => {
    if (!session || !session.sub) {
      console.log("User not logged in");
      tagRemaingToSend.push(...tags_names);
      return;
    }

    const stateTagBuy = localStorage.getItem("tag_manage");
    const date_now = moment().format("YYYY-MM-DD HH");
    const objstateTagBuy = stateTagBuy ? JSON.parse(stateTagBuy) : {};
    const final_tags_to_send = (
      Array.isArray(tags_names) ? tags_names : [tags_names]
    ).filter((tag) => {
      return !objstateTagBuy[session.sub + "_" + date_now]?.hasOwnProperty(tag);
    });

    if (final_tags_to_send.length > 0 || Object.keys(fields_names).length > 0) {
      console.log("final_tags_to_send", final_tags_to_send);
      fetchUpdateTagUser({
        method: "POST",
        url: API_SEND_AUTOMATION,
        data: {
          user_sub: session.sub,
          tags_names: final_tags_to_send,
          fields_names: fields_names,
        },
        onSuccess: (response) => {
          console.log("updateTagUser", response);
          if (final_tags_to_send.length > 0) {
            const update_storage = {
              ...objstateTagBuy,
              [session.sub + "_" + date_now]: {
                ...objstateTagBuy[session.sub + "_" + date_now],
                ...final_tags_to_send.reduce((add, tag) => {
                  return { ...add, [tag]: true };
                }, {}),
              },
            };
            localStorage.setItem("tag_manage", JSON.stringify(update_storage));
          }
        },
      });
    }
  };

  const [errorRule, setErrorRule] = useState(initialErrorRule);
  useEffect(() => {
    if (!session) {
      setInfoUsers({ ...initialInfoUser });
      setCheckError({ ...initialError });
    }
  }, [session]);

  useEffect(() => {
    if (
      session &&
      configs.tour &&
      !configs.tour?.some((obj) => obj.sub === session.sub) &&
      !OnboardingViewed
    ) {
      setOpenModalStart(true);
    } else {
      setOpenModalStart(false);
    }
  }, [session, configs]);

  const [state, dispatch] = useReducer(reducer, initialState);
  const _navigate = useNavigate();

  useEffect(() => {
    setActivePlans(
      session && session.plans.plans.filter((p) => p.status === "active"),
    );
    setProductBase(
      session &&
        session.plans.plans.filter(
          (app) => app.type === "paid" && app.status === "active",
        ),
    );
  }, [session]);

  useEffect(() => {
    if (state.gameUrl) {
      _navigate(R_GAME_SCREEN);
    }
  }, [_navigate, state.gameUrl]);

  const toggleLogged = () => {
    dispatch({
      type: KEY_TOGGLE_LOGGED,
    });
  };

  useEffect(() => {
    if (activePlans && session) {
      const expired = () => {
        if (session.plans?.active_base_plan?.length > 0) {
          return moment(session.plans.active_base_plan[0].next_charge).format(
            "DD/MM/YYYY",
          );
        } else if (session.plans?.last_payment.length > 0) {
          return moment(
            session.plans.last_payment.filter((p) => p.category === "BASE")[0]
              .next_charge,
          ).format("DD/MM/YYYY");
        }
        return null;
      };

      setInfoUsers((prevState) => ({
        ...prevState,
        sub: true,
        isGold:
          activePlans &&
          activePlans.some((item2) => item2.product_id === 2) &&
          activePlans.some((item5) => item5.product_id === 5) &&
          activePlans.find((item2) => item2.product_id === 2).order_id ===
            activePlans.find((item5) => item5.product_id === 5).order_id,
        isSilver:
          activePlans &&
          activePlans.some((item2) => item2.product_id === 2) &&
          activePlans.some((item6) => item6.product_id === 6) &&
          activePlans.find((item2) => item2.product_id === 2).order_id ===
            activePlans.find((item6) => item6.product_id === 6).order_id,
        isBase:
          activePlans &&
          activePlans.some((item2) => item2.product_id === 2) &&
          activePlans.some((item2) => item2.payment_method !== "trial") &&
          !activePlans.some(
            (item5or6) =>
              [5, 6].includes(item5or6.product_id) &&
              item5or6.order_id ===
                activePlans.find((item2) => item2.product_id === 2).order_id,
          ),
        isTrial:
          activePlans &&
          activePlans.find(
            (app) => app.category === "TRIAL" || app.payment_method === "trial",
          )
            ? true
            : false,
        isFree:
          activePlans && activePlans.find((app) => app.product_id === 1)
            ? true
            : false,
        usersTotal:
          session &&
          session.plans?.permissions?.users &&
          session.plans.permissions.users[0],

        dateExpiration: expired(),
        paymentMethod: productBase && productBase[0]?.payment_method,
        hasLastPayment: session?.plans.last_payment.length > 0,
        lastPayment: session?.plans?.last_payment,
        remainingDays: productBase?.[0]?.next_charge
          ? moment(productBase[0].next_charge).diff(moment(), "days")
          : 0,
      }));
      setActualPlan(
        activePlans &&
          activePlans.some((item2) => item2.product_id === 2) &&
          activePlans.some((item5) => item5.product_id === 5) &&
          activePlans.find((item2) => item2.product_id === 2).order_id ===
            activePlans.find((item5) => item5.product_id === 5).order_id
          ? "PlanGold"
          : activePlans &&
              activePlans.some((item2) => item2.product_id === 2) &&
              activePlans.some((item6) => item6.product_id === 6) &&
              activePlans.find((item2) => item2.product_id === 2).order_id ===
                activePlans.find((item6) => item6.product_id === 6).order_id
            ? "PlanSilver"
            : activePlans &&
                activePlans.some((item2) => item2.product_id === 2) &&
                activePlans.some((item2) => item2.payment_method !== "trial") &&
                !activePlans.some(
                  (item5or6) =>
                    [5, 6].includes(item5or6.product_id) &&
                    item5or6.order_id ===
                      activePlans.find((item2) => item2.product_id === 2)
                        .order_id,
                ) &&
                activePlans.filter((plan) => plan.category === "BASE")
              ? "PlanBase"
              : activePlans &&
                  activePlans.some((item2) => item2.product_id > 10)
                ? activePlans[0].name
                : null,
      );
    }
  }, [activePlans, session]);

  //Investigar, pode estar inativo um desses states (Código legado ???)
  const [isFreeCannotPlay, setisFreeCannotPlay] = useState(false);
  const [isFreeCannotPlayState, setisFreeCannotPlayState] = useState(0);

  const setScrollable = (isScrollable = true) => {
    isScrollable = isScrollable === true ? true : false;
    dispatch({
      type: KEY_SET_SCROLLABLE,
      isScrollable,
    });
  };

  const toggleAside = () => {
    let updateAside = !state.isAsideOpen;
    // let updateAside = !state.isAsideOpen || window.innerWidth > 992;
    dispatch({
      type: KEY_TOGGLE_ASIDE,
      updateAside,
      isScrollable: !updateAside,
    });
  };
  const setGameUrl = (url = null) => {
    dispatch({ type: KEY_SET_URL_GAME, url });
  };
  /**
   *
   * @param {string|number} add_path
   * ele aceita dois tipos de valor, string ou numerico.
   * string: ele adicionará o valor a url atual, ex: se a url atual for /comprar e você mandar "turbinar" ele concatenará e ficará /comprar/turbinar
   * Caso você mande com barra no inicio, ele subsituirá a url atual, ex: se a url atual for /comprar e você mandar "/turbinar" ele substituirá e ficará apenas /turbinar
   *
   * caso o parametro for numérico, terá duas ações
   * -1 : ele voltará uma pagina na url
   * 0 : ele voltará uma pagina na url e atualizará a pagina
   * @param {*} paramatersToValues atualizara os parametros da url atravez de um objeto
   * a chave do objeto é o nome do parametro e o valor do objeto é o valor que o parametro
   * você pode enviar varios parametros simultaneo caso precise
   * ex: caso mande {"etapa":"fatura"}, ele adicionará na url, "?etapa=fatura"
   */
  const changePropertyUrl = (
    add_path = "",
    paramatersToValues = {},
    pushOnly = false,
  ) => {
    // 1. Usar _navigate(-1) para voltar via React Router
    if (add_path === "back") {
      _navigate(-1);
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    let haveParamsToAdd =
      add_path === "" && Object.keys(paramatersToValues).length === 0;

    // Corrigido: Para iterar sobre keysSearch corretamente
    if (haveParamsToAdd && searchParams) {
      const keysArray = Array.from(searchParams.keys());
      if (keysArray.length > 0) {
        keysArray.forEach((key) => searchParams.delete(key));
      }
    }

    Object.keys(paramatersToValues).forEach((key) => {
      haveParamsToAdd =
        haveParamsToAdd || searchParams.get(key) !== paramatersToValues[key];
      if (paramatersToValues[key])
        searchParams.set(key, paramatersToValues[key]);
    });

    let newSearch = searchParams.toString();
    newSearch = newSearch ? `?${newSearch}` : newSearch;
    let base_url = window.location.pathname;
    let need_refresh = false;

    if (typeof add_path === "number") {
      base_url = window.location.pathname
        .split("/")
        .slice(0, add_path)
        .join("/");
      if (add_path === 0) need_refresh = true;
      newSearch = "";
      add_path = "";
      haveParamsToAdd = true;
    } else {
      if (add_path.startsWith("/")) base_url = "";
      add_path =
        add_path.includes("/") || add_path === "" ? add_path : "/" + add_path;
    }

    if ((add_path === "" && haveParamsToAdd) || !base_url.endsWith(add_path)) {
      const newUrl = base_url + add_path + newSearch;

      // Se pushOnly for FALSE (ou seja, troca de aba/etapa interna),
      // usamos replace: true para NÃO criar um novo passo no botão voltar do navegador.
      if (newUrl !== window.location.pathname + window.location.search)
        _navigate(newUrl, {
          state: { origin: "internal_flow" },
          replace: !pushOnly,
        });

      if (need_refresh) {
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
    }
  };

  const removeUser = (sub) => {
    setLocalUsers((prev) => (prev ?? []).filter((u) => u.sub !== sub));
  };

  const addUser = (user) => {
    setLocalUsers((prev) => [...(prev ?? []), user]);
  };

  const updateUser = (sub, fields) => {
    setLocalUsers((prev) =>
      (prev ?? []).map((u) => (u.sub === sub ? { ...u, ...fields } : u)),
    );
  };

  return (
    <SystemContext.Provider
      value={{
        ...state,
        toggleLogged,
        changePropertyUrl,
        setScrollable,
        toggleAside,
        setGameUrl,
        openModalStart,
        setOpenModalStart,
        infoUsers,
        updateTagUser,
        initialInfoUser,
        setInfoUsers,
        actualPlan,
        activePlans,
        isRegister,
        setIsRegister,
        tagMapToSend,
        isPurchase,
        setIsPurchase,
        CheckError,
        initialError,
        setCheckError,
        PurchaseInfo,
        setPurchaseInfo,
        UserCards,
        setUserCards,
        PixResponse,
        setPixResponse,
        analytics,
        openModalWaitPayment,
        setOpenModalWaitPayment,
        errorRule,
        initialErrorRule,
        setErrorRule,
        isFreeCannotPlay,
        setPreventFreeOpenGame,
        setisFreeCannotPlayState,
        isFreeCannotPlayState,
        activities,
        NeedUpdateSession,
        setNeedUpdateSession,
        clearTagLocal,
        loginInfo,
        setLoginInfo,
        shopProducts,
        isLoadingShopProducts,
        updateShopProducts,
        currentAvatar,
        setCurrentAvatar,
        accountUser,
        currentUser,
        my_users: localUsers,
        isLoadingUsers,
        isUsersReady: localUsers !== null,
        removeUser,
        addUser,
        updateUser,
        activeUsersCount,
        userAvatar,
      }}
    >
      {children}
      {modalFreeLimit && modalFreeLimit}
    </SystemContext.Provider>
  );
};
export default SystemContext;
